import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faSms, faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  Msg200or201,
  Msg204,
  Msg400,
  Msg401,
  Msg403,
  Msg500,
} from "../../Messages/Messages";
import { useNavigate } from "react-router-dom";
import { decryptData } from "../../../utils/storageCrypto";

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-4">
    <svg
      className="animate-spin h-6 w-6 text-green-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-label="Loading"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v8H4z"
      ></path>
    </svg>
  </div>
);

// Main Share RFQ Popup Component
const ShareRFQPopup = ({
  isOpen,
  onClose,
  rfq,
  vendors = [],
  assignedVendorIds = [],
}) => {
  const [activeShareMethod, setActiveShareMethod] = useState(null); // "whatsapp", "email", "sms"
  const [showDetailModal, setShowDetailModal] = useState(false);

  if (!isOpen) return null;

  const handleIconClick = (method) => {
    setActiveShareMethod(method);
    setShowDetailModal(true);
  };

  return (
    <>
      {/* Main Share Popup */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-rfq-title"
      >
        <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
            aria-label="Close share popup"
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
          </button>
          <h3
            id="share-rfq-title"
            className="text-lg font-semibold text-gray-900 mb-6 text-center"
          >
            Share RFQ
          </h3>
          <div className="flex justify-around">
            <button
              className="flex flex-col items-center gap-1 p-3 hover:bg-gray-100 rounded transition focus:outline-none focus:ring-2 focus:ring-green-500"
              onClick={() => handleIconClick("whatsapp")}
              aria-label="Share via WhatsApp"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 32 32"
                className="h-6 w-6 text-green-600"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M16 0C7.163 0 0 7.163 0 16c0 2.833.739 5.488 2.02 7.77L0 32l8.527-2.015A15.935 15.935 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm7.95 22.67c-.36 1.03-2.02 1.93-2.65 2.05-.7.13-1.55.18-3.55-.86-4.33-2.04-7.11-6.56-7.32-6.86-.21-.31-1.73-2.35-1.73-4.48 0-2.13 1.13-3.18 1.53-3.62.4-.44.89-.56 1.18-.56.31 0 .64.01.92.01.3 0 .7-.12 1.1.9.38 1.01 1.26 3.5 1.37 3.76.12.26.19.57.03.92-.16.36-.24.58-.47.9-.23.32-.48.7-.7.94-.23.25-.46.53-.16 1.05.3.52 1.33 2.2 2.86 3.56 1.97 1.88 3.66 2.39 4.21 2.66.56.27.88.23 1.2-.14.33-.37 1.31-1.52 1.67-2.05.36-.52.73-.45 1.36-.27.63.18 2.02.82 2.37.97.36.14.6.21.69.33.08.12.08.72-.28 1.74z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">WhatsApp</span>
            </button>

            <button
              className="flex flex-col items-center gap-1 p-3 hover:bg-gray-100 rounded transition focus:outline-none focus:ring-2 focus:ring-blue-600"
              onClick={() => handleIconClick("email")}
              aria-label="Share via Email"
              type="button"
            >
              <FontAwesomeIcon
                icon={faEnvelope}
                className="h-6 w-6 text-blue-600"
                aria-hidden="true"
              />
              <span className="text-sm font-medium text-gray-700">Email</span>
            </button>

            <button
              className="flex flex-col items-center gap-1 p-3 hover:bg-gray-100 rounded transition focus:outline-none focus:ring-2 focus:ring-yellow-500"
              onClick={() => handleIconClick("sms")}
              aria-label="Share via SMS"
              type="button"
            >
              <FontAwesomeIcon
                icon={faSms}
                className="h-6 w-6 text-yellow-600"
                aria-hidden="true"
              />
              <span className="text-sm font-medium text-gray-700">SMS</span>
            </button>
          </div>
        </div>
      </div>

      {/* Detailed Share Modal */}
      {showDetailModal && (
        <ShareDetailModal
          method={activeShareMethod}
          onClose={() => setShowDetailModal(false)}
          rfq={rfq}
          vendors={vendors}
          assignedVendorIds={assignedVendorIds}
        />
      )}
    </>
  );
};

// Detailed Share Modal Component
const ShareDetailModal = ({
  method,
  onClose,
  rfq,
  vendors,
  assignedVendorIds,
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("selectVendors"); // "selectVendors" or "addContacts"
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [contacts, setContacts] = useState([{ value: "" }]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [retryFunction, setRetryFunction] = useState(null);

  // Preselect vendors based on assignedVendorIds
  useEffect(() => {
    const vendorIdsToSelect = assignedVendorIds || [];
    const preselected = vendors.filter((v) => vendorIdsToSelect.includes(v._id));
    setSelectedVendors(preselected);
  }, [assignedVendorIds, vendors]);

  // Filter vendors by search query
  const filteredVendors = vendors.filter((v) =>
    v.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Toggle vendor selection
  const toggleVendor = (vendor) => {
    if (selectedVendors.find((v) => v._id === vendor._id)) {
      setSelectedVendors(selectedVendors.filter((v) => v._id !== vendor._id));
    } else {
      setSelectedVendors([...selectedVendors, vendor]);
    }
  };

  // Update contact input value
  const updateContact = (index, value) => {
    const newContacts = [...contacts];
    newContacts[index].value = value;
    setContacts(newContacts);
  };

  // Add new contact input field
  const addContactField = () => {
    setContacts([...contacts, { value: "" }]);
  };

  // Check if Send button should be disabled
  const isSendDisabled =
    isSaving ||
    (activeTab === "selectVendors" && selectedVendors.length === 0) ||
    (activeTab === "addContacts" &&
      contacts.every((c) => c.value.trim() === ""));

  // Close popup handler
  const closePopup = () => {
    setShowPopup(false);
  };

  // Retry handler
  const retry = async () => {
    setShowPopup(false);
    if (retryFunction) {
      setIsSaving(true);
      await retryFunction();
      setIsSaving(false);
    }
  };

  // Logout and redirect handler
  const backToLoginPage = () => {
    setShowPopup(false);
    localStorage.removeItem("LoginData");
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("userId");
    sessionStorage.removeItem("appId");
    navigate("/bb");
  };

  // Send RFQ to suppliers API call
  const sendRequirementToSuppliers = async () => {
    try {
      setIsSaving(true);
      const encryptedToken = sessionStorage.getItem('token');
                const token = decryptData(encryptedToken);

      if (!token) {
        setPopupType("401");
        setPopupMessage("Authentication required. Please log in again.");
        setShowPopup(true);
        setIsSaving(false);
        return;
      }
      const url = process.env.REACT_APP_FORE_BRICKBUCKET;
      const response = await fetch(
        `${url}customer/rfq/request-vendors-for-best-quotes-for-rfq`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
          body: JSON.stringify({
            RFQ_id: rfq._id,
            vendor_ids: selectedVendors.map((v) => v._id),
          }),
        }
      );
      const data = await response.json();

      if (data.status === 200 || data.status === 201) {
        setPopupType("200or201");
        setPopupMessage(data.message || "RFQ sent successfully!");
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
          onClose();
        }, 3000);
      } else if (data.status === 204) {
        setPopupType("204");
        setPopupMessage(data.message || "No content to send.");
        setShowPopup(true);
      } else if (data.status === 400) {
        setPopupType("400");
        setPopupMessage(data.message || "Bad request.");
        setShowPopup(true);
      } else if (data.status === 401) {
        setPopupType("401");
        setPopupMessage("Unauthorized. Please log in again.");
        setShowPopup(true);
      } else if (data.status === 403) {
        setPopupType("403");
        setPopupMessage("Your session has expired. Please log in again.");
        setShowPopup(true);
      } else if (data.status === 404) {
        setPopupType("400");
        setPopupMessage(data.message || "Resource not found.");
        setShowPopup(true);
      } else if (data.status === 500) {
        setPopupType("500");
        setPopupMessage(
          "Internal Server Error. Please try again later or contact support."
        );
        setShowPopup(true);
      } else {
        setPopupType("400");
        setPopupMessage("Unknown error occurred.");
        setShowPopup(true);
      }
    } catch (error) {
      setPopupType("400");
      setPopupMessage("Network error. Please try again.");
      setShowPopup(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Send button click
  const handleSend = async () => {
    if (activeTab === "selectVendors") {
      setRetryFunction(() => sendRequirementToSuppliers);
      await sendRequirementToSuppliers();
    } else {
      // Validate contacts before sending
      const validContacts = contacts
        .map((c) => c.value.trim())
        .filter((v) => v !== "");
      if (validContacts.length === 0) {
        setPopupType("400");
        setPopupMessage("Please enter at least one valid contact.");
        setShowPopup(true);
        return;
      }
      // Here you can implement sending RFQ to contacts logic
      alert(`RFQ sent via ${method} to contacts!`);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-detail-title"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden relative flex flex-col">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition focus:outline-none focus:ring-2 focus:ring-blue-600 rounded"
          aria-label="Close share detail modal"
          type="button"
        >
          <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-gray-200">
          <h3
            id="share-detail-title"
            className="text-xl font-semibold text-gray-900"
          >
            Share RFQ via {method.toUpperCase()}
          </h3>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          <button
            className={`flex-1 py-3 text-center font-medium transition focus:outline-none ${
              activeTab === "selectVendors"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
            onClick={() => setActiveTab("selectVendors")}
            type="button"
            aria-selected={activeTab === "selectVendors"}
            role="tab"
          >
            Select Vendors
          </button>
          <button
            className={`flex-1 py-3 text-center font-medium transition focus:outline-none ${
              activeTab === "addContacts"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
            onClick={() => setActiveTab("addContacts")}
            type="button"
            aria-selected={activeTab === "addContacts"}
            role="tab"
          >
            Add Contacts
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {activeTab === "selectVendors" && (
            <>
              {/* Search Input */}
              <input
                type="text"
                placeholder="Search vendors..."
                className="w-full mb-4 border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search vendors"
              />

              {/* Vendor List */}
              <ul
                className="max-h-[320px] overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-200"
                role="listbox"
                aria-multiselectable="true"
              >
                {filteredVendors.length > 0 ? (
                  filteredVendors.map((vendor) => {
                    const isSelected = selectedVendors.some(
                      (v) => v._id === vendor._id
                    );
                    return (
                      <li
                        key={vendor._id}
                        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${
                          isSelected ? "bg-blue-50" : ""
                        }`}
                        onClick={() => toggleVendor(vendor)}
                        role="option"
                        aria-selected={isSelected}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            toggleVendor(vendor);
                          }
                        }}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleVendor(vendor)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            aria-labelledby={`vendor-label-${vendor._id}`}
                          />
                          <label
                            id={`vendor-label-${vendor._id}`}
                            className="text-gray-900 font-medium select-none cursor-pointer"
                          >
                            {vendor.name}
                          </label>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="p-3 text-center text-gray-500">
                    No vendors found.
                  </li>
                )}
              </ul>
            </>
          )}

                    {activeTab === "addContacts" && (
            <div className="space-y-3">
              {contacts.map((c, idx) => (
                <input
                  key={idx}
                  type={
                    method === "sms" || method === "whatsapp"
                      ? "tel"
                      : method === "email"
                      ? "email"
                      : "text"
                  }
                  placeholder={
                    method === "email" ? "Enter email" : "Enter phone number"
                  }
                  className="w-full border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  value={c.value}
                  onChange={(e) => updateContact(idx, e.target.value)}
                  aria-label={`Contact input ${idx + 1}`}
                />
              ))}
              <button
                type="button"
                className="text-blue-600 text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-blue-600 rounded"
                onClick={addContactField}
              >
                + Add Another
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end items-center space-x-4">
          {isSaving && <LoadingSpinner />}
          <button
            className={`px-6 py-2 rounded-md text-white font-semibold transition focus:outline-none focus:ring-2 focus:ring-green-600 ${
              isSendDisabled
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-600 hover:bg-green-700"
            }`}
            disabled={isSendDisabled}
            onClick={handleSend}
            type="button"
            aria-disabled={isSendDisabled}
          >
            {isSaving ? "Sending..." : "Send"}
          </button>
        </div>

        {/* Popup Messages */}
        {showPopup && (
          <div >
              {popupType === "200or201" && (
                <Msg200or201 message={popupMessage} timeout={3000} />
              )}
              {popupType === "204" && (
                <Msg204 message={popupMessage} timeout={3000} />
              )}
              {popupType === "400" && (
                <Msg400 message={popupMessage} closePopup={closePopup} />
              )}
              {popupType === "401" && (
                <Msg401 message={popupMessage} closePopup={closePopup} />
              )}
              {popupType === "403" && (
                <Msg403 message={popupMessage} closePopup={backToLoginPage} />
              )}
              {popupType === "500" && (
                <Msg500
                  message={popupMessage}
                  closePopup={retry}
                  cancelPopup={closePopup}
                />
              )}
            
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareRFQPopup;
